import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import apinode from '../../services/apinode.js'
import Navbar from '../../components/navbar/navbar.jsx'

import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts'
import { useOrdenation } from '../../hooks/useOrdenation/index.js'

const fmt = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const fmtData = (d) => {
  if (!d) return '-'
  return new Date(d).toLocaleDateString('pt-BR')
}

const IMG_BASE = 'https://node.ziphub.com.br/imagens/'
const POR_PAGINA = 100
const POR_PAG_ITENS = 50

const STATUS_COLORS = {
  Atendido:    { bg: '#d1fae5', color: '#065f46' },
  'Ag. conf.': { bg: '#fef3c7', color: '#92400e' },
  Cancelado:   { bg: '#fee2e2', color: '#991b1b' },
  'Em aberto': { bg: '#dbeafe', color: '#1e40af' },
}

function StatusBadge({ status }) {
  const style = STATUS_COLORS[status] || { bg: '#f3f4f6', color: '#374151' }
  return (
    <span style={{ background: style.bg, color: style.color, borderRadius: 6, padding: '2px 10px', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
      {status || '—'}
    </span>
  )
}

function Card({ label, value, color }) {
  return (
    <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: '14px 16px' }}>
      <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, marginBottom: 4 }}>{label.toUpperCase()}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color }}>{value}</div>
    </div>
  )
}

function ItemRow({ item }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', borderBottom: '1px solid #f1f5f9' }}>
      {item.IMAGEM ? (
        <img src={item.IMAGEM} alt={item.DESCRICAO} style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 6, background: '#f8fafc' }} />
      ) : (
        <div style={{ width: 40, height: 40, borderRadius: 6, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: '#94a3b8' }}>📦</div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 500, fontSize: 13, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.DESCRICAO || item.CODIGO}</div>
        <div style={{ fontSize: 11, color: '#94a3b8' }}>SKU: {item.CODIGO}</div>
      </div>
      <div style={{ textAlign: 'center', minWidth: 50 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>×{item.QUANTIDADE}</div>
        <div style={{ fontSize: 10, color: '#94a3b8' }}>qtd</div>
      </div>
      <div style={{ textAlign: 'right', minWidth: 80 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>{fmt(item.CUSTO_ITEM)}</div>
        <div style={{ fontSize: 10, color: '#94a3b8' }}>custo</div>
      </div>
      <div style={{ minWidth: 70, textAlign: 'center' }}>
        {item.CONFERIDO === 'S'
          ? <span style={{ background: '#d1fae5', color: '#065f46', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>✓ Conf.</span>
          : <span style={{ background: '#fef3c7', color: '#92400e', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>Pendente</span>
        }
      </div>
    </div>
  )
}

function PedidoRow({ pedido, contaMaster, idLoja, highlight }) {
  const [open, setOpen] = useState(false)
  const [itens, setItens] = useState([])
  const [loading, setLoading] = useState(false)

  const loadItens = useCallback(async () => {
    if (itens.length > 0) { setOpen((o) => !o); return }
    setLoading(true)
    try {
      const res = await apinode.get(`/pedido/custo/${contaMaster}/${pedido.PEDIDO}/itens`, {
        params: { ...(idLoja && { id_loja: idLoja }) },
      })
      setItens(res.data?.data || [])
      setOpen(true)
    } catch { setItens([]) }
    finally { setLoading(false) }
  }, [pedido.PEDIDO, contaMaster, idLoja, itens.length])

  const margem = pedido.TOTAL > 0
    ? (((pedido.TOTAL - pedido.CUSTO_TOTAL) / pedido.TOTAL) * 100).toFixed(1)
    : '0.0'

  return (
    <div style={{ background: highlight ? '#faf5ff' : '#fff', borderRadius: 10, border: highlight ? '1px solid #ddd6fe' : '1px solid #e2e8f0', marginBottom: 15, overflow: 'hidden', boxShadow: open ? '0 2px 12px rgba(0,0,0,0.07)' : 'none', transition: 'box-shadow 0.2s' }}>
      <div onClick={loadItens} style={{ display: 'grid', gridTemplateColumns: '1fr 130px 100px 100px 100px 90px 70px 36px', alignItems: 'center', gap: 8, padding: '12px 16px', cursor: 'pointer', userSelect: 'none', background: open ? '#f8fafc' : 'transparent', transition: 'background 0.15s' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>#{pedido.PEDIDO}</div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>{pedido.CLIENTE}</div>
          {pedido.IDPEDIDOLOJA && <div style={{ fontSize: 11, color: '#94a3b8' }}>Loja: {pedido.IDPEDIDOLOJA}</div>}
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 13, color: '#475569' }}>{fmtData(pedido.DATA)}</div>
          <div style={{ fontSize: 10, color: '#94a3b8' }}>{pedido.SHIP || '—'}</div>
        </div>
        <div style={{ textAlign: 'center' }}><StatusBadge status={pedido.STATUS} /></div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>{pedido.QTD_TOTAL || 0}</div>
          <div style={{ fontSize: 10, color: '#94a3b8' }}>{pedido.QTD_ITENS} SKUs</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>{fmt(pedido.TOTAL)}</div>
          <div style={{ fontSize: 10, color: '#94a3b8' }}>+ frete {fmt(pedido.VALOR_FRETE)}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#ef4444' }}>{fmt(pedido.CUSTO_TOTAL)}</div>
          <div style={{ fontSize: 10, color: '#94a3b8' }}>custo</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: Number(margem) >= 20 ? '#059669' : Number(margem) >= 10 ? '#d97706' : '#ef4444' }}>{margem}%</div>
          <div style={{ fontSize: 10, color: '#94a3b8' }}>margem</div>
        </div>
        <div style={{ fontSize: 16, color: '#94a3b8', transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', textAlign: 'center' }}>▼</div>
      </div>
      {(open || loading) && (
        <div style={{ borderTop: '1px solid #e2e8f0' }}>
          {loading ? (
            <div style={{ padding: '16px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Carregando itens...</div>
          ) : itens.length === 0 ? (
            <div style={{ padding: '16px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Nenhum item encontrado.</div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 12, padding: '6px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <div style={{ width: 40 }} />
                <div style={{ flex: 1, fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Produto</div>
                <div style={{ width: 50, fontSize: 11, fontWeight: 600, color: '#64748b', textAlign: 'center' }}>Qtd</div>
                <div style={{ width: 80, fontSize: 11, fontWeight: 600, color: '#64748b', textAlign: 'right' }}>Custo</div>
                <div style={{ width: 70, fontSize: 11, fontWeight: 600, color: '#64748b', textAlign: 'center' }}>Status</div>
              </div>
              {itens.map((item) => <ItemRow key={item.ID} item={item} />)}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 24, padding: '10px 16px', background: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>Total venda</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>{fmt(pedido.TOTAL)}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>Custo total</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#ef4444' }}>{fmt(pedido.CUSTO_TOTAL)}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>Lucro bruto</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#059669' }}>{fmt(pedido.TOTAL - pedido.CUSTO_TOTAL)}</div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

function Paginacao({ pagina, total, porPagina, onChange }) {
  const totalPaginas = Math.ceil(total / porPagina)
  if (totalPaginas <= 1) return null
  const btnStyle = (ativo) => ({ padding: '6px 12px', borderRadius: 6, border: '1px solid #e2e8f0', background: ativo ? '#3b82f6' : '#fff', color: ativo ? '#fff' : '#475569', fontWeight: ativo ? 700 : 400, fontSize: 13, cursor: ativo ? 'default' : 'pointer', minWidth: 36, textAlign: 'center' })
  const paginas = []
  for (let i = 1; i <= totalPaginas; i++) {
    if (i === 1 || i === totalPaginas || (i >= pagina - 2 && i <= pagina + 2)) paginas.push(i)
    else if (paginas[paginas.length - 1] !== '...') paginas.push('...')
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '16px 0', flexWrap: 'wrap' }}>
      <button onClick={() => onChange(pagina - 1)} disabled={pagina === 1} style={{ ...btnStyle(false), opacity: pagina === 1 ? 0.4 : 1 }}>‹</button>
      {paginas.map((p, i) => p === '...'
        ? <span key={`e-${i}`} style={{ color: '#94a3b8', padding: '0 4px' }}>…</span>
        : <button key={p} onClick={() => onChange(p)} style={btnStyle(p === pagina)}>{p}</button>
      )}
      <button onClick={() => onChange(pagina + 1)} disabled={pagina === totalPaginas} style={{ ...btnStyle(false), opacity: pagina === totalPaginas ? 0.4 : 1 }}>›</button>
      <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 8 }}>
        {(pagina - 1) * porPagina + 1}–{Math.min(pagina * porPagina, total)} de {total}
      </span>
    </div>
  )
}

function TabBI({ pedidos, contas }) {
  const porFilial = {}
  for (const p of pedidos) {
    const loja = p.ID_LOJA || p.IDPEDIDOLOJA || '—'
    const apelido = contas.find((c) => String(c.ID_CONTA) === String(loja))?.APELIDO || loja
    if (!porFilial[apelido]) porFilial[apelido] = { apelido, venda: 0, custo: 0, lucro: 0, qtd: 0 }
    porFilial[apelido].venda += Number(p.TOTAL || 0)
    porFilial[apelido].custo += Number(p.CUSTO_TOTAL || 0)
    porFilial[apelido].lucro += Number(p.TOTAL || 0) - Number(p.CUSTO_TOTAL || 0)
    porFilial[apelido].qtd += 1
  }
  const filiais = Object.values(porFilial).sort((a, b) => b.custo - a.custo)
  const porDia = {}
  for (const p of pedidos) {
    const dia = fmtData(p.DATA)
    if (!porDia[dia]) porDia[dia] = { dia, venda: 0, custo: 0, lucro: 0 }
    porDia[dia].venda += Number(p.TOTAL || 0)
    porDia[dia].custo += Number(p.CUSTO_TOTAL || 0)
    porDia[dia].lucro += Number(p.TOTAL || 0) - Number(p.CUSTO_TOTAL || 0)
  }
  const evolucao = Object.values(porDia).reverse()
  const fmtK = (v) => `R$ ${(v / 1000).toFixed(1)}k`

  return (
    <div>
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: 20, marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 16 }}>Custo por filial — ranking</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['#', 'Filial', 'Pedidos', 'Venda total', 'Custo total', 'Lucro', 'Margem'].map((h, i) => (
                  <th key={i} style={{ padding: '8px 12px', textAlign: i <= 1 ? 'left' : 'right', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filiais.map((f, i) => {
                const margem = f.venda > 0 ? ((f.lucro / f.venda) * 100).toFixed(1) : '0.0'
                return (
                  <tr key={f.apelido} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '10px 12px', color: '#94a3b8', fontWeight: 700 }}>{i + 1}</td>
                    <td style={{ padding: '10px 12px', fontWeight: 700, color: '#1e293b' }}>{f.apelido}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: '#475569' }}>{f.qtd}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: '#1e293b', fontWeight: 600 }}>{fmt(f.venda)}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: '#ef4444', fontWeight: 600 }}>{fmt(f.custo)}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: '#059669', fontWeight: 600 }}>{fmt(f.lucro)}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: Number(margem) >= 20 ? '#059669' : Number(margem) >= 10 ? '#d97706' : '#ef4444' }}>{margem}%</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: 20, marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 16 }}>Venda × Custo por filial</div>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={filiais} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="apelido" tick={{ fontSize: 11 }} />
            <YAxis tickFormatter={fmtK} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v) => fmt(v)} />
            <Legend />
            <Bar dataKey="venda" name="Venda" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            <Bar dataKey="custo" name="Custo" fill="#ef4444" radius={[4, 4, 0, 0]} />
            <Bar dataKey="lucro" name="Lucro" fill="#059669" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 16 }}>Evolução diária — Venda / Custo / Lucro</div>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={evolucao} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="dia" tick={{ fontSize: 10 }} />
            <YAxis tickFormatter={fmtK} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v) => fmt(v)} />
            <Legend />
            <Line type="monotone" dataKey="venda" name="Venda" stroke="#3b82f6" dot={false} strokeWidth={2} />
            <Line type="monotone" dataKey="custo" name="Custo" stroke="#ef4444" dot={false} strokeWidth={2} />
            <Line type="monotone" dataKey="lucro" name="Lucro" stroke="#059669" dot={false} strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

function TabTrocas({ trocas, contaMaster, idLoja }) {
  const [pagina, setPagina] = useState(1)
  const totalCustoTrocas = trocas.reduce((a, p) => a + Number(p.CUSTO_TOTAL || 0), 0)
  const totalVendaTrocas = trocas.reduce((a, p) => a + Number(p.TOTAL || 0), 0)
  const trocasPagina = trocas.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA)

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
        <Card label="Qtd. trocas"          value={trocas.length}         color="#8b5cf6" />
        <Card label="Custo total trocas"   value={fmt(totalCustoTrocas)} color="#ef4444" />
        <Card label="Abatimento no acerto" value={fmt(totalCustoTrocas)} color="#059669" />
      </div>
      {trocas.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 130px 100px 100px 100px 90px 70px 36px', gap: 8, padding: '6px 16px', marginBottom: 4 }}>
          {['Pedido / Cliente', 'Data', 'Status', 'Qtd', 'Valor', 'Custo', 'Margem', ''].map((h, i) => (
            <div key={i} style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', textAlign: i >= 3 ? 'center' : 'left' }}>{h}</div>
          ))}
        </div>
      )}
      {trocas.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', color: '#94a3b8', fontSize: 14 }}>Nenhuma troca encontrada no período.</div>
      ) : (
        <>
          {trocasPagina.map((p) => <PedidoRow key={p.PEDIDO} pedido={p} contaMaster={contaMaster} idLoja={idLoja} highlight={true} />)}
          <Paginacao pagina={pagina} total={trocas.length} porPagina={POR_PAGINA} onChange={setPagina} />
          <div style={{ background: '#faf5ff', border: '1px solid #ddd6fe', borderRadius: 10, padding: '16px 20px', marginTop: 8, display: 'flex', justifyContent: 'flex-end', gap: 40, alignItems: 'center' }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 2 }}>Valor venda das trocas</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#475569' }}>{fmt(totalVendaTrocas)}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 2 }}>Custo total das trocas</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#ef4444' }}>{fmt(totalCustoTrocas)}</div>
            </div>
            <div style={{ textAlign: 'right', background: '#fff', borderRadius: 8, padding: '10px 18px', border: '1px solid #ddd6fe' }}>
              <div style={{ fontSize: 11, color: '#7c3aed', fontWeight: 600, marginBottom: 2 }}>ABATIMENTO NO ACERTO</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#059669' }}>- {fmt(totalCustoTrocas)}</div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

const GRUPO_OPTS = [
  { value: 'none',    label: 'Sem agrupamento' },
  { value: 'sku',     label: 'Por SKU / Produto' },
  { value: 'data',    label: 'Por Data' },
  { value: 'cliente', label: 'Por Cliente' },
  { value: 'status',  label: 'Por Status do Pedido' },
]

function TabItens({ contaMaster, idLoja, data1, data2, tipo, status: statusInicial }) {
  const [itens, setItens]       = useState([])
  const [loading, setLoading]   = useState(false)
  const [erro, setErro]         = useState('')
  const [busca, setBusca]       = useState('')
  const [conferido, setConferido] = useState('')
  const [status, setStatus]     = useState(statusInicial || '')
  const [pagina, setPagina]     = useState(1)
  const [grupoBy, setGrupoBy]   = useState('none')

  const buscarItens = useCallback(async () => {
    setLoading(true); setErro(''); setPagina(1)
    try {
      const res = await apinode.get(`/pedido/custo/${contaMaster}/itens`, {
        params: {
          ...(idLoja    && { id_loja: idLoja }),
          ...(tipo      && tipo !== 'TODOS' && { tipo }),
          ...(status    && { status }),
          ...(data1     && { data1 }),
          ...(data2     && { data2 }),
          ...(busca     && { busca }),
          ...(conferido && { conferido }),
        },
      })
      setItens(res.data?.data || [])
    } catch (e) {
      setErro(e.response?.data?.error || 'Erro ao buscar itens.')
      setItens([])
    } finally { setLoading(false) }
  }, [contaMaster, idLoja, tipo, status, data1, data2, busca, conferido])

  const itensFiltrados = useMemo(() => {
    if (grupoBy === 'none') return itens
    const keyFn   = { sku: (i) => i.CODIGO, data: (i) => fmtData(i.DATA), cliente: (i) => i.CLIENTE || '—', status: (i) => i.STATUS_PEDIDO || '—' }[grupoBy]
    const labelFn = { sku: (i) => ({ label: i.DESCRICAO || i.CODIGO, sub: i.CODIGO, img: i.IMAGEM }), data: (i) => ({ label: fmtData(i.DATA), sub: null, img: null }), cliente: (i) => ({ label: i.CLIENTE || '—', sub: null, img: null }), status: (i) => ({ label: i.STATUS_PEDIDO || '—', sub: null, img: null }) }[grupoBy]
    const map = {}
    for (const item of itens) {
      const key = keyFn(item)
      if (!map[key]) map[key] = { ...labelFn(item), key, CODIGO: item.CODIGO, PRECOCUSTO: item.PRECOCUSTO, QUANTIDADE: 0, CUSTO_ITEM: 0, _count: 0, _pedidos: new Set(), _skus: new Set() }
      map[key].QUANTIDADE += Number(item.QUANTIDADE || 0)
      map[key].CUSTO_ITEM += Number(item.CUSTO_ITEM  || 0)
      map[key]._count += 1
      map[key]._pedidos.add(item.PEDIDO)
      map[key]._skus.add(item.CODIGO)
    }
    return Object.values(map).map((g) => ({ ...g, _pedidosCount: g._pedidos.size, _skusCount: g._skus.size })).sort((a, b) => b.CUSTO_ITEM - a.CUSTO_ITEM)
  }, [itens, grupoBy])

  const totalItens   = itensFiltrados.length
  const totalQtd     = itensFiltrados.reduce((s, i) => s + Number(i.QUANTIDADE || 0), 0)
  const totalCusto   = itensFiltrados.reduce((s, i) => s + Number(i.CUSTO_ITEM  || 0), 0)
  const totalPaginas = Math.ceil(totalItens / POR_PAG_ITENS)
  const itensPagina  = itensFiltrados.slice((pagina - 1) * POR_PAG_ITENS, pagina * POR_PAG_ITENS)
  const agrupado     = grupoBy !== 'none'

  const inputStyle = { border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '7px 11px', fontSize: 13, color: '#333', background: '#fafafa', outline: 'none' }

  const handleCSV = () => {
    const rows = agrupado
      ? [['Grupo', 'Qtd Itens', 'Pedidos', 'SKUs', 'Qtd Total', 'Custo Total']]
      : [['Pedido', 'Cliente', 'Data', 'Status Pedido', 'SKU', 'Produto', 'Qtd', 'Custo Unit.', 'Custo Total', 'Conferido']]
    for (const i of itensFiltrados) {
      if (agrupado) rows.push([i.label, i._count, i._pedidosCount, i._skusCount, i.QUANTIDADE, Number(i.CUSTO_ITEM || 0).toFixed(2)])
      else rows.push([i.PEDIDO, i.CLIENTE, fmtData(i.DATA), i.STATUS_PEDIDO, i.CODIGO, i.DESCRICAO, i.QUANTIDADE, Number(i.PRECOCUSTO || 0).toFixed(2), Number(i.CUSTO_ITEM || 0).toFixed(2), i.CONFERIDO === 'S' ? 'Sim' : 'Não'])
    }
    const csv  = rows.map((r) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(';')).join('\n')
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href = url; a.download = `itens_${grupoBy}_${data1}_${data2}.csv`; a.click()
    URL.revokeObjectURL(url)
  }

  const colDef = agrupado
    ? { cols: '44px 1fr 100px 80px 80px 100px',          headers: ['', 'Grupo', 'Pedidos', 'SKUs', 'Qtd', 'Custo total'] }
    : { cols: '44px 1fr 200px 90px 80px 90px 90px 80px', headers: ['', 'Produto / SKU', 'Pedido / Cliente', 'Data', 'Qtd', 'Custo unit.', 'Custo total', 'Conf.'] }

  return (
    <div>
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: '14px 16px', marginBottom: 14, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 4 }}>BUSCAR SKU / PRODUTO / PEDIDO</label>
          <input value={busca} onChange={(e) => setBusca(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && buscarItens()} placeholder="SKU, descrição ou nº pedido..." style={{ ...inputStyle, width: 260 }} />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 4 }}>STATUS PEDIDO</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)} style={inputStyle}>
            <option value="">Todos</option><option value="Atendido">Atendido</option><option value="Ag. conf.">Ag. conf.</option><option value="Em aberto">Em aberto</option><option value="Cancelado">Cancelado</option>
          </select>
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 4 }}>CONFERÊNCIA</label>
          <select value={conferido} onChange={(e) => setConferido(e.target.value)} style={inputStyle}>
            <option value="">Todos</option><option value="S">Conferido</option><option value="N">Pendente</option>
          </select>
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 4 }}>AGRUPAR POR</label>
          <select value={grupoBy} onChange={(e) => { setGrupoBy(e.target.value); setPagina(1) }} style={{ ...inputStyle, minWidth: 180 }}>
            {GRUPO_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <button onClick={buscarItens} disabled={loading} style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#3b82f6', color: '#fff', fontSize: 13, fontWeight: 700, cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.6 : 1 }}>
          {loading ? 'Buscando...' : 'Buscar Itens'}
        </button>
        {itens.length > 0 && (
          <button onClick={handleCSV} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#059669' }}>📊 CSV</button>
        )}
      </div>

      {erro && <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#991b1b', fontSize: 13, marginBottom: 12 }}>⚠️ {erro}</div>}

      {itens.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 14 }}>
          {[
            { label: agrupado ? `Grupos (${GRUPO_OPTS.find((o) => o.value === grupoBy)?.label})` : 'Linhas', value: totalItens,      color: '#3b82f6' },
            { label: 'Itens brutos',  value: itens.length,    color: '#64748b' },
            { label: 'Qtd total',     value: totalQtd,        color: '#1e293b' },
            { label: 'Custo total',   value: fmt(totalCusto), color: '#ef4444' },
          ].map((c, i) => (
            <div key={i} style={{ background: '#fff', borderRadius: 10, padding: '12px 16px', border: '1px solid #e2e8f0', borderLeft: `4px solid ${c.color}` }}>
              <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, marginBottom: 3, textTransform: 'uppercase' }}>{c.label}</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: c.color }}>{c.value}</div>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8', fontSize: 14 }}>Carregando itens...</div>
      ) : itens.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', color: '#94a3b8', fontSize: 14 }}>Use os filtros acima e clique em "Buscar Itens".</div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: colDef.cols, gap: 8, padding: '10px 16px', background: '#F7F8FA', borderBottom: '2px solid #EBEBEB' }}>
            {colDef.headers.map((h, i) => <div key={i} style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>{h}</div>)}
          </div>

          {agrupado
            ? itensPagina.map((g, idx) => (
                <div key={`g-${idx}`} style={{ display: 'grid', gridTemplateColumns: colDef.cols, gap: 8, padding: '10px 16px', borderBottom: '1px solid #F2F2F2', alignItems: 'center', transition: 'background 0.1s' }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#FAFBFC')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  {g.img ? <img src={`${IMG_BASE}${g.img}`} alt="" onError={(e) => { e.target.src = `${IMG_BASE}no-image.jpg` }} style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6, border: '1px solid #e2e8f0' }} />
                    : <div style={{ width: 40, height: 40, borderRadius: 6, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: '#94a3b8' }}>
                        {grupoBy === 'data' ? '📅' : grupoBy === 'cliente' ? '👤' : grupoBy === 'status' ? '🏷' : '📦'}
                      </div>}
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{g.label}</div>
                    {g.sub && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{g.sub}</div>}
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{g._count} linha(s) de item</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#3b82f6' }}>{g._pedidosCount}</div>
                    <div style={{ fontSize: 10, color: '#94a3b8' }}>pedidos</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#8b5cf6' }}>{g._skusCount}</div>
                    <div style={{ fontSize: 10, color: '#94a3b8' }}>SKUs</div>
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: '#1e293b', textAlign: 'center' }}>{g.QUANTIDADE}</div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: '#ef4444', textAlign: 'right' }}>{fmt(g.CUSTO_ITEM)}</div>
                </div>
              ))
            : itensPagina.map((item, idx) => (
                <div key={`${item.ID || item.CODIGO}-${idx}`} style={{ display: 'grid', gridTemplateColumns: colDef.cols, gap: 8, padding: '10px 16px', borderBottom: '1px solid #F2F2F2', alignItems: 'center', transition: 'background 0.1s' }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#FAFBFC')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <img src={item.IMAGEM ? `${IMG_BASE}${item.IMAGEM}` : `${IMG_BASE}no-image.jpg`} alt="" onError={(e) => { e.target.src = `${IMG_BASE}no-image.jpg` }} style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6, border: '1px solid #e2e8f0' }} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.DESCRICAO || '—'}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{item.CODIGO}</div>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#3b82f6' }}>#{item.PEDIDO}</div>
                    <div style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.CLIENTE}</div>
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>{fmtData(item.DATA)}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', textAlign: 'center' }}>{item.QUANTIDADE}</div>
                  <div style={{ fontSize: 13, color: '#64748b', textAlign: 'right' }}>{fmt(item.PRECOCUSTO)}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#ef4444', textAlign: 'right' }}>{fmt(item.CUSTO_ITEM)}</div>
                  <div style={{ textAlign: 'center' }}>
                    {item.CONFERIDO === 'S'
                      ? <span style={{ background: '#d1fae5', color: '#065f46', borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>✓</span>
                      : <span style={{ background: '#fef3c7', color: '#92400e', borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>—</span>}
                  </div>
                </div>
              ))}

          <div style={{ display: 'grid', gridTemplateColumns: colDef.cols, gap: 8, padding: '12px 16px', background: '#F7F8FA', borderTop: '2px solid #EBEBEB' }}>
            <div /><div style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>TOTAL</div>
            {agrupado ? (<><div /><div /><div style={{ textAlign: 'center', fontWeight: 800, color: '#1e293b' }}>{totalQtd}</div><div style={{ textAlign: 'right', fontWeight: 800, color: '#ef4444' }}>{fmt(totalCusto)}</div></>)
              : (<><div /><div /><div style={{ textAlign: 'center', fontWeight: 800, color: '#1e293b' }}>{totalQtd}</div><div /><div style={{ textAlign: 'right', fontWeight: 800, color: '#ef4444' }}>{fmt(totalCusto)}</div><div /></>)}
          </div>
        </div>
      )}

      {totalPaginas > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 6, padding: '14px 0', alignItems: 'center' }}>
          <button onClick={() => setPagina((p) => p - 1)} disabled={pagina === 1} style={{ padding: '5px 14px', borderRadius: 7, border: '1px solid #e2e8f0', background: '#fff', cursor: pagina === 1 ? 'default' : 'pointer', fontSize: 13, opacity: pagina === 1 ? 0.4 : 1 }}>← Ant.</button>
          <span style={{ fontSize: 13, color: '#64748b' }}>{pagina} / {totalPaginas} — {totalItens} {agrupado ? 'grupos' : 'itens'}</span>
          <button onClick={() => setPagina((p) => p + 1)} disabled={pagina === totalPaginas} style={{ padding: '5px 14px', borderRadius: 7, border: '1px solid #e2e8f0', background: '#fff', cursor: pagina === totalPaginas ? 'default' : 'pointer', fontSize: 13, opacity: pagina === totalPaginas ? 0.4 : 1 }}>Próx. →</button>
        </div>
      )}
    </div>
  )
}

function TabAcerto({ pedidos, trocas, contas, idLoja, data1, data2 }) {
  const printRef   = useRef()
  const conta      = contas.find((c) => String(c.ID_CONTA) === String(idLoja))
  const apelido    = conta?.APELIDO || idLoja
  const totalVenda  = pedidos.reduce((a, p) => a + Number(p.TOTAL       || 0), 0)
  const totalCusto  = pedidos.reduce((a, p) => a + Number(p.CUSTO_TOTAL || 0), 0)
  const totalTrocas = trocas.reduce ((a, p) => a + Number(p.CUSTO_TOTAL || 0), 0)
  const totalLiquido = totalCusto - totalTrocas
  const totalLucro   = totalVenda - totalCusto
  const margem       = totalVenda > 0 ? ((totalLucro / totalVenda) * 100).toFixed(1) : '0.0'

  const handlePrint = () => {
    const janela = window.open('', '_blank')
    janela.document.write(`<html><head><title>Acerto — ${apelido}</title><style>body{font-family:Arial,sans-serif;font-size:12px;margin:20px}h1{font-size:18px}h2{font-size:14px;color:#555;margin-bottom:16px}table{width:100%;border-collapse:collapse;margin-bottom:16px}th{background:#f3f4f6;padding:6px 8px;font-size:11px;text-transform:uppercase;border-bottom:2px solid #ddd}td{padding:6px 8px;border-bottom:1px solid #eee}</style></head><body>${printRef.current.innerHTML}</body></html>`)
    janela.document.close(); janela.focus(); janela.print()
  }

  const handleCSV = () => {
    const rows = [['Pedido','Data','Cliente','Status','Tipo','Qtd Itens','Qtd Total','Valor Venda','Valor Frete','Total','Custo Total','Lucro','Margem %']]
    for (const p of [...pedidos, ...trocas]) {
      const lucro = Number(p.TOTAL || 0) - Number(p.CUSTO_TOTAL || 0)
      const mg    = p.TOTAL > 0 ? ((lucro / p.TOTAL) * 100).toFixed(1) : '0.0'
      rows.push([p.PEDIDO, fmtData(p.DATA), p.CLIENTE, p.STATUS, p.TIPO_PEDIDO || 'VENDA', p.QTD_ITENS, p.QTD_TOTAL, Number(p.VALOR || 0).toFixed(2), Number(p.VALOR_FRETE || 0).toFixed(2), Number(p.TOTAL || 0).toFixed(2), Number(p.CUSTO_TOTAL || 0).toFixed(2), lucro.toFixed(2), mg])
    }
    rows.push([], ['RESUMO'], ['Custo bruto','','','','','','','','','',totalCusto.toFixed(2)], ['Abatimento trocas','','','','','','','','','',`-${totalTrocas.toFixed(2)}`], ['VALOR LÍQUIDO A COBRAR','','','','','','','','','',totalLiquido.toFixed(2)])
    const csv  = rows.map((r) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(';')).join('\n')
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href = url; a.download = `acerto_${apelido}_${data1}_${data2}.csv`; a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, justifyContent: 'flex-end' }}>
        <button onClick={handlePrint} style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#475569' }}>🖨️ Imprimir</button>
        <button onClick={handleCSV}   style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: '#059669', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>📊 Excel/CSV</button>
      </div>
      <div ref={printRef}>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>Relatório de Acerto</h1>
        <h2 style={{ margin: '4px 0 16px', fontSize: 13, color: '#64748b', fontWeight: 400 }}>
          Filial: <strong>{apelido}</strong> &nbsp;|&nbsp; Período: <strong>{fmtData(data1 + 'T12:00:00')}</strong> a <strong>{fmtData(data2 + 'T12:00:00')}</strong> &nbsp;|&nbsp; {pedidos.length} pedidos {trocas.length > 0 && `| ${trocas.length} trocas`}
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 20 }}>
          {[
            { label: 'Total pedidos',                   value: pedidos.length, color: '#3b82f6', fn: (v) => v },
            { label: 'Venda total',                     value: totalVenda,     color: '#1e293b', fn: fmt },
            { label: 'Custo bruto',                     value: totalCusto,     color: '#ef4444', fn: fmt },
            { label: `Abat. trocas (${trocas.length})`, value: totalTrocas,    color: '#8b5cf6', fn: (v) => `- ${fmt(v)}` },
            { label: 'Líquido a cobrar',                value: totalLiquido,   color: '#059669', fn: fmt },
          ].map((c, i) => (
            <div key={i} style={{ background: i === 4 ? '#f0fdf4' : '#fff', borderRadius: 10, border: i === 4 ? '2px solid #86efac' : '1px solid #e2e8f0', padding: '14px 16px' }}>
              <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, marginBottom: 4 }}>{c.label.toUpperCase()}</div>
              <div style={{ fontSize: i === 4 ? 22 : 20, fontWeight: 800, color: c.color }}>{c.fn(c.value)}</div>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>Pedidos</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 20 }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              {['Pedido','Data','Cliente','Status','Qtd','Valor venda','Frete','Total','Custo','Lucro','Margem'].map((h, i) => (
                <th key={i} style={{ padding: '8px 10px', textAlign: i >= 4 ? 'right' : 'left', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pedidos.map((p) => {
              const lucro = Number(p.TOTAL || 0) - Number(p.CUSTO_TOTAL || 0)
              const mg    = p.TOTAL > 0 ? ((lucro / p.TOTAL) * 100).toFixed(1) : '0.0'
              return (
                <tr key={p.PEDIDO} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '8px 10px', fontWeight: 700, color: '#1e293b' }}>#{p.PEDIDO}</td>
                  <td style={{ padding: '8px 10px', color: '#475569' }}>{fmtData(p.DATA)}</td>
                  <td style={{ padding: '8px 10px', color: '#475569', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.CLIENTE}</td>
                  <td style={{ padding: '8px 10px' }}><StatusBadge status={p.STATUS} /></td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{p.QTD_TOTAL}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{fmt(p.VALOR)}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', color: '#64748b' }}>{fmt(p.VALOR_FRETE)}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700 }}>{fmt(p.TOTAL)}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', color: '#ef4444', fontWeight: 600 }}>{fmt(p.CUSTO_TOTAL)}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', color: '#059669', fontWeight: 600 }}>{fmt(lucro)}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: Number(mg) >= 20 ? '#059669' : Number(mg) >= 10 ? '#d97706' : '#ef4444' }}>{mg}%</td>
                </tr>
              )
            })}
          </tbody>
          <tfoot>
            <tr style={{ background: '#f8fafc', borderTop: '2px solid #e2e8f0' }}>
              <td colSpan={4} style={{ padding: '10px 10px', fontWeight: 700, fontSize: 13 }}>SUBTOTAL — {pedidos.length} pedidos</td>
              <td style={{ padding: '10px 10px', textAlign: 'right', fontWeight: 700 }}>{pedidos.reduce((a, p) => a + Number(p.QTD_TOTAL || 0), 0)}</td>
              <td style={{ padding: '10px 10px', textAlign: 'right', fontWeight: 700 }}>{fmt(pedidos.reduce((a, p) => a + Number(p.VALOR || 0), 0))}</td>
              <td style={{ padding: '10px 10px', textAlign: 'right', fontWeight: 700 }}>{fmt(pedidos.reduce((a, p) => a + Number(p.VALOR_FRETE || 0), 0))}</td>
              <td style={{ padding: '10px 10px', textAlign: 'right', fontWeight: 700 }}>{fmt(totalVenda)}</td>
              <td style={{ padding: '10px 10px', textAlign: 'right', fontWeight: 700, color: '#ef4444' }}>{fmt(totalCusto)}</td>
              <td style={{ padding: '10px 10px', textAlign: 'right', fontWeight: 700, color: '#059669' }}>{fmt(totalLucro)}</td>
              <td style={{ padding: '10px 10px', textAlign: 'right', fontWeight: 700, color: Number(margem) >= 20 ? '#059669' : Number(margem) >= 10 ? '#d97706' : '#ef4444' }}>{margem}%</td>
            </tr>
          </tfoot>
        </table>
        {trocas.length > 0 && (
          <>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#7c3aed', marginBottom: 8, marginTop: 4 }}>🔄 Trocas — abatimento do acerto</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 20 }}>
              <thead>
                <tr style={{ background: '#faf5ff' }}>
                  {['Pedido','Data','Cliente','Status','Qtd','Valor venda','Frete','Total','Custo (abate)','Lucro','Margem'].map((h, i) => (
                    <th key={i} style={{ padding: '8px 10px', textAlign: i >= 4 ? 'right' : 'left', fontSize: 11, fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', borderBottom: '2px solid #ddd6fe' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {trocas.map((p) => {
                  const lucro = Number(p.TOTAL || 0) - Number(p.CUSTO_TOTAL || 0)
                  const mg    = p.TOTAL > 0 ? ((lucro / p.TOTAL) * 100).toFixed(1) : '0.0'
                  return (
                    <tr key={p.PEDIDO} style={{ borderBottom: '1px solid #ede9fe', background: '#faf5ff' }}>
                      <td style={{ padding: '8px 10px', fontWeight: 700, color: '#7c3aed' }}>#{p.PEDIDO}</td>
                      <td style={{ padding: '8px 10px', color: '#475569' }}>{fmtData(p.DATA)}</td>
                      <td style={{ padding: '8px 10px', color: '#475569', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.CLIENTE}</td>
                      <td style={{ padding: '8px 10px' }}><StatusBadge status={p.STATUS} /></td>
                      <td style={{ padding: '8px 10px', textAlign: 'right' }}>{p.QTD_TOTAL}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'right' }}>{fmt(p.VALOR)}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', color: '#64748b' }}>{fmt(p.VALOR_FRETE)}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700 }}>{fmt(p.TOTAL)}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', color: '#7c3aed', fontWeight: 700 }}>- {fmt(p.CUSTO_TOTAL)}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', color: '#059669', fontWeight: 600 }}>{fmt(lucro)}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: Number(mg) >= 20 ? '#059669' : Number(mg) >= 10 ? '#d97706' : '#ef4444' }}>{mg}%</td>
                    </tr>
                  )
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: '#ede9fe', borderTop: '2px solid #ddd6fe' }}>
                  <td colSpan={8} style={{ padding: '10px 10px', fontWeight: 700, fontSize: 13, color: '#7c3aed' }}>ABATIMENTO TROCAS — {trocas.length} pedidos</td>
                  <td style={{ padding: '10px 10px', textAlign: 'right', fontWeight: 800, color: '#7c3aed', fontSize: 14 }}>- {fmt(totalTrocas)}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </>
        )}
        <div style={{ background: '#f0fdf4', border: '2px solid #86efac', borderRadius: 10, padding: '16px 20px', display: 'flex', justifyContent: 'flex-end', gap: 40, alignItems: 'center' }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 2 }}>Custo bruto dos pedidos</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#ef4444' }}>{fmt(totalCusto)}</div>
          </div>
          {trocas.length > 0 && (
            <>
              <div style={{ fontSize: 20, color: '#94a3b8' }}>−</div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 2 }}>Abatimento trocas ({trocas.length})</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#7c3aed' }}>{fmt(totalTrocas)}</div>
              </div>
              <div style={{ fontSize: 20, color: '#94a3b8' }}>=</div>
            </>
          )}
          <div style={{ textAlign: 'right', background: '#fff', borderRadius: 8, padding: '12px 20px', border: '2px solid #86efac' }}>
            <div style={{ fontSize: 11, color: '#059669', fontWeight: 700, marginBottom: 2 }}>VALOR LÍQUIDO A COBRAR</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: '#059669' }}>{fmt(totalLiquido)}</div>
          </div>
        </div>
        <div style={{ marginTop: 16, fontSize: 11, color: '#94a3b8', borderTop: '1px solid #e2e8f0', paddingTop: 10 }}>
          Gerado em {new Date().toLocaleString('pt-BR')} &nbsp;|&nbsp; Filial: <strong>{apelido}</strong> &nbsp;|&nbsp; Custo bruto: <strong style={{ color: '#ef4444' }}>{fmt(totalCusto)}</strong>
          {trocas.length > 0 && <> &nbsp;−&nbsp; Trocas: <strong style={{ color: '#7c3aed' }}>{fmt(totalTrocas)}</strong></>}
          &nbsp;=&nbsp; Valor a cobrar: <strong style={{ color: '#059669', fontSize: 14 }}>{fmt(totalLiquido)}</strong>
        </div>
      </div>
    </div>
  )
}

// ── Tab Conferidos ─────────────────────────────────────────────────────────────
const CONF_GRUPO_OPTS = [
  { value: 'none',    label: 'Sem agrupamento',        icon: '—'  },
  { value: 'marca',   label: 'Por Marca',               icon: '🏷️' },
  { value: 'sku',     label: 'Por SKU / Produto',       icon: '📦' },
  { value: 'data',    label: 'Por Data de Conferência', icon: '📅' },
  { value: 'cliente', label: 'Por Cliente',             icon: '👤' },
  { value: 'status',  label: 'Por Status do Pedido',    icon: '🔖' },
]

// ── Sub-tabela de itens de uma marca ──────────────────────────────────────────
function MarcaItensExpanded({ itensDAMarca }) {
  const totalCusto = itensDAMarca.reduce((s, i) => s + Number(i.CUSTO_ITEM || 0), 0)
  const totalQtd   = itensDAMarca.reduce((s, i) => s + Number(i.QTDE       || 0), 0)

  return (
    <div style={{ background: '#f0fdf4', borderTop: '1px solid #BBF7D0', borderBottom: '1px solid #BBF7D0' }}>
      {/* Mini-header */}
      <div style={{ display: 'grid', gridTemplateColumns: '44px 1fr 140px 110px 60px 90px 80px 100px', gap: 8, padding: '7px 16px 7px 32px', background: '#DCFCE7', borderBottom: '1px solid #BBF7D0' }}>
        {['', 'Produto / SKU', 'Pedido / Cliente', 'Data Conf.', 'Qtd', 'Data Pedido', 'Status', 'Custo item'].map((h, i) => (
          <div key={i} style={{ fontSize: 10, fontWeight: 700, color: '#065f46', textTransform: 'uppercase' }}>{h}</div>
        ))}
      </div>

      {/* Linhas */}
      {itensDAMarca.map((item, idx) => (
        <div key={`mi-${idx}`}
          style={{ display: 'grid', gridTemplateColumns: '44px 1fr 140px 110px 60px 90px 80px 100px', gap: 8, padding: '9px 16px 9px 32px', borderBottom: '1px solid #DCFCE7', alignItems: 'center', transition: 'background 0.1s' }}
          onMouseEnter={(e) => (e.currentTarget.style.background = '#d1fae5')}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
        >
          <img src={item.IMAGEM ? `${IMG_BASE}${item.IMAGEM}` : `${IMG_BASE}no-image.jpg`} alt=""
            onError={(e) => { e.target.src = `${IMG_BASE}no-image.jpg` }}
            style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 5, border: '1.5px solid #BBF7D0' }}
          />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 12, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.DESCRICAO || '—'}</div>
            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 1 }}>{item.SKU}</div>
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 12, color: '#3b82f6' }}>#{item.PEDIDO}</div>
            <div style={{ fontSize: 10, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.CLIENTE}</div>
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#059669' }}>{fmtData(item.DATA_CONF)}</div>
            <div style={{ fontSize: 9, color: '#94a3b8' }}>conferido</div>
          </div>
          <div style={{ textAlign: 'center', fontSize: 13, fontWeight: 800, color: '#1e293b' }}>{item.QTDE}</div>
          <div style={{ fontSize: 11, color: '#64748b' }}>{fmtData(item.DATA_PEDIDO)}</div>
          <div><StatusBadge status={item.STATUS} /></div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#ef4444' }}>{fmt(item.CUSTO_ITEM)}</div>
            <div style={{ fontSize: 9, color: '#94a3b8' }}>{fmt(item.PRECOCUSTO)}/un</div>
          </div>
        </div>
      ))}

      {/* Mini-rodapé */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 24, padding: '8px 16px', background: '#DCFCE7' }}>
        <span style={{ fontSize: 12, color: '#065f46' }}>Qtd total: <strong>{totalQtd}</strong></span>
        <span style={{ fontSize: 12, color: '#065f46' }}>Custo total: <strong style={{ color: '#ef4444' }}>{fmt(totalCusto)}</strong></span>
      </div>
    </div>
  )
}

function TabConferidos({ contaMaster, idLoja, data1, data2 }) {
  const [itens, setItens]         = useState([])
  const [loading, setLoading]     = useState(false)
  const [erro, setErro]           = useState('')
  const [busca, setBusca]         = useState('')
  const [dataConf1, setDataConf1] = useState(data1)
  const [dataConf2, setDataConf2] = useState(data2)
  const [pagina, setPagina]       = useState(1)
  const [grupoBy, setGrupoBy]     = useState('none')
  // ── collapse state para agrupamento Por Marca ─────────────────────────────
  const [expandedMarcas, setExpandedMarcas] = useState(new Set())

  const toggleMarca = (key) => {
    setExpandedMarcas((prev) => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }

  const buscar = useCallback(async () => {
    setLoading(true); setErro(''); setPagina(1); setExpandedMarcas(new Set())
    try {
      const res = await apinode.get(`/pedido/conferidos/${contaMaster}`, {
        params: {
          ...(idLoja    && { id_loja: idLoja }),
          ...(dataConf1 && { data1: dataConf1 }),
          ...(dataConf2 && { data2: dataConf2 }),
          ...(busca     && { busca }),
        },
      })
      setItens(res.data?.data || [])
    } catch (e) {
      setErro(e.response?.data?.error || 'Erro ao buscar conferidos.')
      setItens([])
    } finally { setLoading(false) }
  }, [contaMaster, idLoja, dataConf1, dataConf2, busca])

  const itensFiltrados = useMemo(() => {
    if (grupoBy === 'none') return itens
    const keyFn = {
      marca:   (i) => i.DESC_MARCA || 'Sem marca',
      sku:     (i) => i.SKU,
      data:    (i) => fmtData(i.DATA_CONF),
      cliente: (i) => i.CLIENTE || '—',
      status:  (i) => i.STATUS  || '—',
    }[grupoBy]

    const map = {}
    for (const item of itens) {
      const key = keyFn(item)
      if (!map[key]) {
        map[key] = {
          key,
          label:      grupoBy === 'sku' ? (item.DESCRICAO || item.SKU) : key,
          sub:        grupoBy === 'sku' ? item.SKU : null,
          IMAGEM:     grupoBy === 'sku' ? item.IMAGEM : null,
          PRECOCUSTO: item.PRECOCUSTO,
          CUSTO_TOTAL: 0,
          _count:  0,
          _pedidos: new Set(),
          _skus:    new Set(),
          _marcas:  new Set(),
        }
      }
      map[key].CUSTO_TOTAL += Number(item.CUSTO_ITEM || 0)
      map[key]._count      += 1
      map[key]._pedidos.add(item.PEDIDO)
      map[key]._skus.add(item.SKU)
      map[key]._marcas.add(item.DESC_MARCA || 'Sem marca')
    }
    return Object.values(map).map((g) => ({ ...g, _pedidosCount: g._pedidos.size, _skusCount: g._skus.size, _marcasCount: g._marcas.size })).sort((a, b) => b.CUSTO_TOTAL - a.CUSTO_TOTAL)
  }, [itens, grupoBy])

  const agrupado   = grupoBy !== 'none'
  const totalItens = itensFiltrados.length
  const totalCusto = agrupado
    ? itensFiltrados.reduce((s, g) => s + g.CUSTO_TOTAL, 0)
    : itens.reduce((s, i) => s + Number(i.CUSTO_ITEM || 0), 0)
  const totalPags  = Math.ceil(totalItens / POR_PAG_ITENS)
  const pagItens   = itensFiltrados.slice((pagina - 1) * POR_PAG_ITENS, pagina * POR_PAG_ITENS)

  const { filteredItems, Th } = useOrdenation(pagItens, agrupado ? 'CUSTO_TOTAL' : 'CUSTO_ITEM')

  const inputStyle = { border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '7px 11px', fontSize: 13, color: '#333', background: '#fafafa', outline: 'none' }

  const handleCSV = () => {
    const rows = agrupado
      ? [['Grupo', 'Sub', 'Itens', 'Pedidos', 'SKUs', 'Custo Total']]
      : [['Data Conf.', 'Pedido', 'Cliente', 'SKU', 'Produto', 'Marca', 'Qtd', 'Custo Unit.', 'Custo Item', 'Data Pedido', 'Status']]
    for (const i of itensFiltrados) {
      if (agrupado) rows.push([i.label, i.sub || '', i._count, i._pedidosCount, i._skusCount, Number(i.CUSTO_TOTAL || 0).toFixed(2)])
      else rows.push([fmtData(i.DATA_CONF), i.PEDIDO, i.CLIENTE, i.SKU, i.DESCRICAO, i.DESC_MARCA || '', i.QTDE, Number(i.PRECOCUSTO || 0).toFixed(2), Number(i.CUSTO_ITEM || 0).toFixed(2), fmtData(i.DATA_PEDIDO), i.STATUS])
    }
    const csv  = rows.map((r) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(';')).join('\n')
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href = url; a.download = `conferidos_${grupoBy}_${dataConf1}_${dataConf2}.csv`; a.click()
    URL.revokeObjectURL(url)
  }

  const grupoIcon  = CONF_GRUPO_OPTS.find((o) => o.value === grupoBy)?.icon  || '—'
  const grupoLabel = CONF_GRUPO_OPTS.find((o) => o.value === grupoBy)?.label || ''

  const colDef = agrupado
    ? { cols: '44px 1fr 90px 90px 90px 90px 36px',        headers: ['', 'Grupo', 'Itens', 'Pedidos', 'SKUs', 'Custo total', ''] }
    : { cols: '44px 1fr 140px 110px 60px 90px 80px 100px', headers: ['', 'Produto / SKU', 'Pedido / Cliente', 'Data Conf.', 'Qtd', 'Data Pedido', 'Status', 'Custo item'] }

  return (
    <div>
      {/* Filtros */}
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: '14px 16px', marginBottom: 14, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 4 }}>BUSCAR SKU / PRODUTO / PEDIDO</label>
          <input value={busca} onChange={(e) => setBusca(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && buscar()} placeholder="SKU, produto ou nº pedido..." style={{ ...inputStyle, width: 240 }} />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 4 }}>DATA CONF. DE</label>
          <input type="date" value={dataConf1} onChange={(e) => setDataConf1(e.target.value)} style={inputStyle} />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 4 }}>DATA CONF. ATÉ</label>
          <input type="date" value={dataConf2} onChange={(e) => setDataConf2(e.target.value)} style={inputStyle} />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 4 }}>AGRUPAR POR</label>
          <select value={grupoBy} onChange={(e) => { setGrupoBy(e.target.value); setPagina(1); setExpandedMarcas(new Set()) }} style={{ ...inputStyle, minWidth: 200 }}>
            {CONF_GRUPO_OPTS.map((o) => <option key={o.value} value={o.value}>{o.icon} {o.label}</option>)}
          </select>
        </div>
        <button onClick={buscar} disabled={loading} style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#059669', color: '#fff', fontSize: 13, fontWeight: 700, cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.6 : 1 }}>
          {loading ? 'Buscando...' : 'Buscar Conferidos'}
        </button>
        {itens.length > 0 && (
          <button onClick={handleCSV} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#059669' }}>📊 CSV</button>
        )}
      </div>

      {erro && <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#991b1b', fontSize: 13, marginBottom: 12 }}>⚠️ {erro}</div>}

      {itens.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 14 }}>
          {[
            { label: agrupado ? `Grupos (${grupoLabel})` : 'Itens conferidos', value: totalItens,                          color: '#059669' },
            { label: 'Total bruto',                                             value: itens.length,                        color: '#64748b' },
            { label: 'SKUs únicos',                                             value: new Set(itens.map((i) => i.SKU)).size, color: '#3b82f6' },
            { label: 'Custo total',                                             value: fmt(totalCusto),                     color: '#ef4444' },
          ].map((c, i) => (
            <div key={i} style={{ background: '#fff', borderRadius: 10, padding: '12px 16px', border: '1px solid #e2e8f0', borderLeft: `4px solid ${c.color}` }}>
              <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, marginBottom: 3, textTransform: 'uppercase' }}>{c.label}</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: c.color }}>{c.value}</div>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8', fontSize: 14 }}>Carregando...</div>
      ) : itens.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', color: '#94a3b8', fontSize: 14 }}>Use os filtros e clique em "Buscar Conferidos".</div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          {/* Header */}
          <div style={{ display: 'grid', gridTemplateColumns: colDef.cols, gap: 8, padding: '10px 16px', background: '#F0FDF4', borderBottom: '2px solid #BBF7D0' }}>
            {colDef.headers.map((h, i) => (
              <div key={i} style={{ fontSize: 11, fontWeight: 700, color: '#065f46', textTransform: 'uppercase' }}>
                {i + 1 < colDef.headers.length
                  ? <span>{h}</span>
                  : <Th label={h} value={agrupado ? 'CUSTO_TOTAL' : 'CUSTO_ITEM'} style={{ all: 'unset', cursor: 'pointer' }} styleLabel={{ fontSize: 11, fontWeight: 700, color: '#8b8b8b', textTransform: 'uppercase', cursor: 'pointer' }} />
                }
              </div>
            ))}
          </div>

          {/* ── Linhas agrupadas ── */}
          {agrupado
            ? filteredItems.map((g, idx) => {
                const isMarca    = grupoBy === 'marca'
                const isExpanded = isMarca && expandedMarcas.has(g.key)
                const itensGrupo = isMarca ? itens.filter((i) => (i.DESC_MARCA || 'Sem marca') === g.key) : []

                return (
                  <div key={`cg-${idx}`}>
                    {/* Linha do grupo */}
                    <div
                      onClick={isMarca ? () => toggleMarca(g.key) : undefined}
                      style={{ display: 'grid', gridTemplateColumns: colDef.cols, gap: 8, padding: '11px 16px', borderBottom: isExpanded ? 'none' : '1px solid #F2F2F2', alignItems: 'center', transition: 'background 0.1s', cursor: isMarca ? 'pointer' : 'default', background: isExpanded ? '#f0fdf4' : 'transparent' }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = '#f0fdf4')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = isExpanded ? '#f0fdf4' : 'transparent')}
                    >
                      {/* thumb */}
                      {g.IMAGEM && grupoBy === 'sku'
                        ? <img src={`${IMG_BASE}${g.IMAGEM}`} alt="" onError={(e) => { e.target.src = `${IMG_BASE}no-image.jpg` }} style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6, border: '2px solid #d1fae5' }} />
                        : <div style={{ width: 40, height: 40, borderRadius: 6, background: '#d1fae5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>{grupoIcon}</div>
                      }
                      {/* label */}
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{g.label}</div>
                        {g.sub && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{g.sub}</div>}
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                          {g._count} conferência(s)
                          {grupoBy !== 'marca' && g._marcasCount > 0 && (
                            <span style={{ marginLeft: 8, background: '#fef3c7', color: '#92400e', borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 600 }}>{g._marcasCount} marca(s)</span>
                          )}
                        </div>
                      </div>
                      {/* itens */}
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: '#059669' }}>{g._count}</div>
                        <div style={{ fontSize: 10, color: '#94a3b8' }}>itens</div>
                      </div>
                      {/* pedidos */}
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: '#3b82f6' }}>{g._pedidosCount}</div>
                        <div style={{ fontSize: 10, color: '#94a3b8' }}>pedidos</div>
                      </div>
                      {/* skus */}
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: '#8b5cf6' }}>{g._skusCount}</div>
                        <div style={{ fontSize: 10, color: '#94a3b8' }}>SKUs</div>
                      </div>
                      {/* custo */}
                      <div style={{ fontSize: 14, fontWeight: 800, color: '#ef4444', textAlign: 'right' }}>{fmt(g.CUSTO_TOTAL)}</div>
                      {/* chevron — só para marca */}
                      <div style={{ textAlign: 'center', fontSize: 14, color: '#059669', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                        {isMarca ? '▼' : ''}
                      </div>
                    </div>

                    {/* ── Itens expandidos (só Por Marca) ── */}
                    {isMarca && isExpanded && <MarcaItensExpanded itensDAMarca={itensGrupo} />}
                  </div>
                )
              })

            /* ── Linhas normais ── */
            : filteredItems.map((item, idx) => (
                <div key={`conf-${idx}`}
                  style={{ display: 'grid', gridTemplateColumns: colDef.cols, gap: 8, padding: '10px 16px', borderBottom: '1px solid #F2F2F2', alignItems: 'center', transition: 'background 0.1s' }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#f0fdf4')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <img src={item.IMAGEM ? `${IMG_BASE}${item.IMAGEM}` : `${IMG_BASE}no-image.jpg`} alt="" onError={(e) => { e.target.src = `${IMG_BASE}no-image.jpg` }} style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6, border: '2px solid #d1fae5' }} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.DESCRICAO || '—'}</div>
                    <div style={{ display: 'flex', gap: 4, marginTop: 2, alignItems: 'center', flexWrap: 'nowrap' }}>
                      <span style={{ fontSize: 10, color: '#94a3b8' }}>{item.SKU}</span>
                      {item.DESC_MARCA && <span style={{ background: '#fef3c7', color: '#92400e', borderRadius: 4, padding: '1px 5px', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' }}>{item.DESC_MARCA}</span>}
                    </div>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#3b82f6' }}>#{item.PEDIDO}</div>
                    <div style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.CLIENTE}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#059669' }}>{fmtData(item.DATA_CONF)}</div>
                    <div style={{ fontSize: 10, color: '#94a3b8' }}>conferido</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: '#1e293b' }}>{item.QTDE}</div>
                    <div style={{ fontSize: 10, color: '#94a3b8' }}>qtd</div>
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>{fmtData(item.DATA_PEDIDO)}</div>
                  <div><StatusBadge status={item.STATUS} /></div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#ef4444' }}>{fmt(item.CUSTO_ITEM)}</div>
                    <div style={{ fontSize: 10, color: '#94a3b8' }}>{fmt(item.PRECOCUSTO)}/un</div>
                  </div>
                </div>
              ))
          }

          {/* Rodapé */}
          <div style={{ display: 'grid', gridTemplateColumns: colDef.cols, gap: 8, padding: '12px 16px', background: '#F0FDF4', borderTop: '2px solid #BBF7D0' }}>
            <div />
            <div style={{ fontSize: 12, fontWeight: 700, color: '#065f46' }}>TOTAL — {agrupado ? `${totalItens} grupos / ${itens.length} itens` : `${itens.length} itens`}</div>
            {agrupado ? (<><div /><div /><div /><div style={{ textAlign: 'right', fontWeight: 800, color: '#ef4444' }}>{fmt(totalCusto)}</div><div /></>)
              : (<><div /><div /><div /><div /><div /><div style={{ textAlign: 'right', fontWeight: 800, color: '#ef4444' }}>{fmt(totalCusto)}</div></>)}
          </div>
        </div>
      )}

      {totalPags > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 6, padding: '14px 0', alignItems: 'center' }}>
          <button onClick={() => setPagina((p) => p - 1)} disabled={pagina === 1} style={{ padding: '5px 14px', borderRadius: 7, border: '1px solid #e2e8f0', background: '#fff', cursor: pagina === 1 ? 'default' : 'pointer', fontSize: 13, opacity: pagina === 1 ? 0.4 : 1 }}>← Ant.</button>
          <span style={{ fontSize: 13, color: '#64748b' }}>{pagina} / {totalPags} — {totalItens} {agrupado ? 'grupos' : 'itens'}</span>
          <button onClick={() => setPagina((p) => p + 1)} disabled={pagina === totalPags} style={{ padding: '5px 14px', borderRadius: 7, border: '1px solid #e2e8f0', background: '#fff', cursor: pagina === totalPags ? 'default' : 'pointer', fontSize: 13, opacity: pagina === totalPags ? 0.4 : 1 }}>Próx. →</button>
        </div>
      )}
    </div>
  )
}

// ── Página principal ───────────────────────────────────────────────────────────
export default function PedidosPage() {
  const contaMaster = localStorage.idconta
  const [contas, setContas]               = useState([])
  const [loadingContas, setLoadingContas] = useState(true)
  const [idLoja, setIdLoja]               = useState('')
  const [tab, setTab]                     = useState('pedidos')
  const [pedidos, setPedidos]             = useState([])
  const [trocas, setTrocas]               = useState([])
  const [loading, setLoading]             = useState(false)
  const [busca, setBusca]                 = useState('')
  const [status, setStatus]               = useState('')
  const [tipo, setTipo]                   = useState('VENDA')
  const [data1, setData1]                 = useState(() => new Date().toISOString().slice(0, 10))
  const [data2, setData2]                 = useState(() => new Date().toISOString().slice(0, 10))
  const [erro, setErro]                   = useState('')
  const [pagina, setPagina]               = useState(1)

  useEffect(() => {
    ;(async () => {
      try {
        const res   = await apinode.get(`/pedido/contas/${contaMaster}`)
        const lista = Array.isArray(res.data) ? res.data : Array.isArray(res.data?.dados) ? res.data.dados : Array.isArray(res.data?.data) ? res.data.data : []
        setContas(lista)
        if (lista.length > 0) setIdLoja(String(lista[0].ID_CONTA))
      } catch (e) { console.error('Erro ao carregar contas', e) }
      finally { setLoadingContas(false) }
    })()
  }, [])

  const buscarPedidos = useCallback(async () => {
    if (!contaMaster) return
    setLoading(true); setErro(''); setPagina(1)
    try {
      const res = await apinode.get(`/pedido/custo/${contaMaster}`, {
        params: { ...(idLoja && { id_loja: idLoja }), ...(tipo && tipo !== 'TODOS' && { tipo }), ...(status && { status }), ...(busca && { busca }), ...(data1 && { data1 }), ...(data2 && { data2 }) },
      })
      if (!res.data.success) throw new Error(res.data.error)
      setPedidos(res.data.data || [])
      const resTrocas = await apinode.get(`/pedido/custo/${contaMaster}`, {
        params: { ...(idLoja && { id_loja: idLoja }), tipo: 'TROCA', ...(data1 && { data1 }), ...(data2 && { data2 }) },
      })
      setTrocas(resTrocas.data?.data || [])
    } catch (e) {
      setErro(e.response?.data?.error || e.message)
      setPedidos([]); setTrocas([])
    } finally { setLoading(false) }
  }, [contaMaster, idLoja, tipo, status, busca, data1, data2])

  useEffect(() => { if (idLoja) buscarPedidos() }, [idLoja])

  const pedidosPagina = pedidos.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA)
  const totalVenda    = pedidos.reduce((a, p) => a + Number(p.TOTAL       || 0), 0)
  const totalCusto    = pedidos.reduce((a, p) => a + Number(p.CUSTO_TOTAL || 0), 0)
  const totalTrocas   = trocas.reduce ((a, p) => a + Number(p.CUSTO_TOTAL || 0), 0)
  const totalLucro    = totalVenda - totalCusto
  const totalLiquido  = totalCusto - totalTrocas
  const margemGeral   = totalVenda > 0 ? ((totalLucro / totalVenda) * 100).toFixed(1) : '0.0'

  const tabStyle = (t) => ({
    padding: '8px 20px', borderRadius: 6, border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer',
    background: tab === t ? (t === 'trocas' ? '#8b5cf6' : t === 'itens' ? '#0ea5e9' : t === 'conferidos' ? '#059669' : '#3b82f6') : '#fff',
    color: tab === t ? '#fff' : '#64748b',
    outline: tab === t ? 'none' : '1px solid #e2e8f0',
  })

  return (
    <>
      <Navbar />
      <div style={{ fontFamily: "'Inter', sans-serif", background: '#f1f5f9', minHeight: '100vh', padding: 24 }}>
        <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0f172a' }}>Pedidos & Acerto</h1>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button style={tabStyle('pedidos')}    onClick={() => setTab('pedidos')}>📦 Pedidos</button>
            <button style={tabStyle('trocas')}     onClick={() => setTab('trocas')}>
              🔄 Trocas {trocas.length > 0 && <span style={{ background: 'rgba(255,255,255,0.3)', borderRadius: 10, padding: '1px 7px', marginLeft: 4, fontSize: 11 }}>{trocas.length}</span>}
            </button>
            <button style={tabStyle('itens')}      onClick={() => setTab('itens')}>📋 Itens</button>
            <button style={tabStyle('conferidos')} onClick={() => setTab('conferidos')}>✅ Conferidos</button>
            <button style={tabStyle('bi')}         onClick={() => setTab('bi')}>📊 BI</button>
            <button style={tabStyle('acerto')}     onClick={() => setTab('acerto')}>🧾 Acerto</button>
          </div>
        </div>

        {/* Filtros globais */}
        <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: '16px', marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '0 0 160px' }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>CONTA</label>
            <select value={idLoja} onChange={(e) => setIdLoja(e.target.value)} disabled={loadingContas} style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13, outline: 'none', background: '#fff', color: '#1e293b' }}>
              {loadingContas ? <option>Carregando...</option> : contas.length === 0 ? <option value="">Nenhuma conta</option> : contas.map((c) => <option key={c.ID_CONTA} value={String(c.ID_CONTA)}>{c.APELIDO || c.ID_CONTA}</option>)}
            </select>
          </div>
          <div style={{ flex: '0 0 140px' }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>TIPO</label>
            <select value={tipo} onChange={(e) => setTipo(e.target.value)} style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13, outline: 'none', background: '#fff' }}>
              <option value="TODOS">Todos</option><option value="VENDA">Venda</option><option value="AGRUPADO">Agrupado</option><option value="COMPRA">Compra</option><option value="CONDICIONAL">Condicional</option><option value="FILIAL">Filial</option><option value="REPOSICAO">Reposição</option><option value="TROCA">Troca</option>
            </select>
          </div>
          <div style={{ flex: '0 0 130px' }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>STATUS</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13, outline: 'none', background: '#fff' }}>
              <option value="">Todos</option><option value="Atendido">Atendido</option><option value="Ag. conf.">Ag. conf.</option><option value="Em aberto">Em aberto</option><option value="Cancelado">Cancelado</option>
            </select>
          </div>
          <div style={{ flex: '1 1 160px' }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>BUSCA</label>
            <input value={busca} onChange={(e) => setBusca(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && buscarPedidos()} placeholder="Pedido, cliente, ID loja..." style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>DE</label>
            <input type="date" value={data1} onChange={(e) => setData1(e.target.value)} style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13, outline: 'none' }} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>ATÉ</label>
            <input type="date" value={data2} onChange={(e) => setData2(e.target.value)} style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13, outline: 'none' }} />
          </div>
          <button onClick={buscarPedidos} disabled={loading} style={{ padding: '8px 20px', borderRadius: 6, border: 'none', background: '#3b82f6', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: loading ? 0.5 : 1 }}>
            {loading ? 'Buscando...' : 'Buscar'}
          </button>
        </div>

        {/* Totalizadores */}
        {(pedidos.length > 0 || trocas.length > 0) && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 16 }}>
            <Card label="Pedidos"                            value={pedidos.length}          color="#3b82f6" />
            <Card label="Venda total"                        value={fmt(totalVenda)}          color="#1e293b" />
            <Card label="Custo bruto"                        value={fmt(totalCusto)}          color="#ef4444" />
            <Card label={`Abat. trocas (${trocas.length})`} value={`- ${fmt(totalTrocas)}`} color="#8b5cf6" />
            <Card label="Líquido a cobrar"                   value={fmt(totalLiquido)}        color="#059669" />
          </div>
        )}

        {erro && <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#991b1b', fontSize: 13, marginBottom: 12 }}>⚠️ {erro}</div>}

        {tab === 'pedidos' && (
          <>
            {pedidos.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 130px 100px 100px 100px 90px 70px 36px', gap: 8, padding: '6px 16px', marginBottom: 4 }}>
                {['Pedido / Cliente','Data','Status','Qtd','Valor','Custo','Margem',''].map((h, i) => (
                  <div key={i} style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', textAlign: i >= 3 ? 'center' : 'left' }}>{h}</div>
                ))}
              </div>
            )}
            {loading ? <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8', fontSize: 14 }}>Carregando pedidos...</div>
              : pedidos.length === 0 ? <div style={{ textAlign: 'center', padding: 48, background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', color: '#94a3b8', fontSize: 14 }}>Nenhum pedido encontrado. Use os filtros acima para buscar.</div>
              : (<>{pedidosPagina.map((p) => <PedidoRow key={p.PEDIDO} pedido={p} contaMaster={contaMaster} idLoja={idLoja} />)}<Paginacao pagina={pagina} total={pedidos.length} porPagina={POR_PAGINA} onChange={setPagina} /></>)
            }
          </>
        )}

        {tab === 'trocas' && (loading
          ? <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8', fontSize: 14 }}>Carregando...</div>
          : <TabTrocas trocas={trocas} contaMaster={contaMaster} idLoja={idLoja} />
        )}

        {tab === 'itens' && <TabItens contaMaster={contaMaster} idLoja={idLoja} data1={data1} data2={data2} tipo={tipo} status={status} />}

        {tab === 'conferidos' && <TabConferidos contaMaster={contaMaster} idLoja={idLoja} data1={data1} data2={data2} />}

        {tab === 'bi' && pedidos.length > 0 && <TabBI pedidos={pedidos} contas={contas} />}
        {tab === 'bi' && pedidos.length === 0 && !loading && <div style={{ textAlign: 'center', padding: 48, background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', color: '#94a3b8', fontSize: 14 }}>Busque pedidos para visualizar o BI.</div>}

        {tab === 'acerto' && pedidos.length > 0 && <TabAcerto pedidos={pedidos} trocas={trocas} contas={contas} idLoja={idLoja} data1={data1} data2={data2} />}
        {tab === 'acerto' && pedidos.length === 0 && !loading && <div style={{ textAlign: 'center', padding: 48, background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', color: '#94a3b8', fontSize: 14 }}>Busque pedidos para gerar o relatório de acerto.</div>}
      </div>
    </>
  )
}